import {
  review_requestor_application_document,
  review_requestor_application_question_document,
  upload_requestor_application_documents,
} from '@/services/requestor_portal_service';
import { normalize_text } from './view_case_details_helpers';

const IMAGE_EXTENSIONS = ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'HEIC'];
const VIDEO_EXTENSIONS = ['MP4', 'MOV', 'AVI'];
const AUDIO_EXTENSIONS = ['MP3', 'WAV', 'M4A', 'AAC'];
const PREVIEW_EXTENSIONS = ['PDF', ...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS, ...AUDIO_EXTENSIONS];

export default {
  choose_document_file() {
    if (this.uploading_document) {
      return;
    }

    this.$refs.document_upload_input?.click();
  },
  async upload_document(event) {
    const file = selected_file(event);
    clear_file_input(event);

    if (!can_upload_file(this, file)) {
      return;
    }

    await upload_file(this, file);
  },
  open_document(document = {}) {
    this.selected_document = document;
    this.document_review_mode = '';
    this.document_review_comment = normalize_text(document?.verification_comment);
  },
  close_document_preview() {
    this.selected_document = null;
    this.document_review_mode = '';
    this.document_review_comment = '';
  },
  download_document(document = {}) {
    if (!document?.url || typeof window === 'undefined') {
      return;
    }

    window.open(document.url, '_blank', 'noopener,noreferrer');
  },
  can_review_document(document = {}) {
    return supported_review_source(document) && has_review_identifiers(document);
  },
  start_document_rejection(document = {}) {
    if (!this.can_review_document(document) || this.submitting_document_review) {
      return;
    }

    this.selected_document = document;
    this.document_review_mode = 'reject';
    this.document_review_comment = normalize_text(this.document_review_comment || document?.verification_comment);
  },
  cancel_document_rejection() {
    this.document_review_mode = '';
    this.document_review_comment = normalize_text(this.selected_document?.verification_comment);
  },
  async review_document(document = {}, is_verified = true) {
    if (!this.can_review_document(document) || this.submitting_document_review) {
      return;
    }

    const verification_comment = normalize_text(this.document_review_comment);
    if (!is_verified) {
      if (!verification_comment) {
        this.documents_error = 'Please enter a rejection reason before rejecting this document.';
        this.document_review_mode = 'reject';
        return;
      }

      this.document_review_mode = '';
    }

    this.submitting_document_review = true;
    this.documents_error = '';
    this.documents_success = '';

    try {
      await submit_document_review(document, is_verified, verification_comment);
      this.documents_success = is_verified
        ? 'Document verified successfully.'
        : 'Document rejected successfully.';
      await this.load_documents();
      this.close_document_preview();
    } catch (error) {
      this.documents_error = upload_error_message(error).replace('upload', 'review');
    } finally {
      this.submitting_document_review = false;
    }
  },
  can_preview_document(document = {}) {
    return Boolean(document?.url && PREVIEW_EXTENSIONS.includes(document_extension(document)));
  },
  is_image_document(document = {}) {
    return IMAGE_EXTENSIONS.includes(document_extension(document));
  },
  is_pdf_document(document = {}) {
    return document_extension(document) === 'PDF';
  },
  is_video_document(document = {}) {
    return VIDEO_EXTENSIONS.includes(document_extension(document));
  },
  is_audio_document(document = {}) {
    return AUDIO_EXTENSIONS.includes(document_extension(document));
  },
};

function selected_file(event) {
  const [file] = Array.from(event?.target?.files || []);
  return file;
}

async function submit_document_review(document, is_verified, verification_comment) {
  if (document?.source === 'additional') {
    return review_requestor_application_document({
      additional_document_id: document.additional_document_id || document.id,
      is_verified,
      verification_comment,
    });
  }

  return review_requestor_application_question_document({
    application_form_id: document.application_form_id,
    section_id: document.section_id,
    question_id: document.question_id,
    is_verified,
    verification_comment,
  });
}

function supported_review_source(document = {}) {
  return ['additional', 'application'].includes(normalize_text(document?.source).toLowerCase());
}

function has_review_identifiers(document = {}) {
  if (normalize_text(document?.source).toLowerCase() === 'additional') {
    return Boolean(document?.additional_document_id || document?.id);
  }

  return Boolean(document?.application_form_id && document?.section_id && document?.question_id);
}

function clear_file_input(event) {
  if (event?.target) {
    event.target.value = '';
  }
}

function can_upload_file(component, file) {
  return Boolean(file && component.selected_application_id);
}

async function upload_file(component, file) {
  reset_upload_state(component);
  try {
    await upload_requestor_application_documents(upload_request(component, file));
    component.documents_success = 'Document uploaded successfully.';
    await component.load_documents();
  } catch (error) {
    component.documents_error = upload_error_message(error);
  } finally {
    component.uploading_document = false;
  }
}

function reset_upload_state(component) {
  component.uploading_document = true;
  component.upload_progress = 0;
  component.documents_error = '';
  component.documents_success = '';
}

function build_upload_request_payload(component, file) {
  const payload = {
    application_id: component.selected_application_id,
    files: [file],
    document_names: [file.name],
    on_upload_progress: (progress_event) => update_upload_progress(component, progress_event),
  };
  return payload;
}

function upload_request(component, file) {
  return build_upload_request_payload(component, file);
}

function update_upload_progress(component, progress_event) {
  if (!progress_event.total) {
    return;
  }

  component.upload_progress = Math.round((progress_event.loaded * 100) / progress_event.total);
}

function upload_error_message(error) {
  return [
    error?.response?.data?.message,
    error?.response?.data?.detail,
    error?.message,
    'Failed to upload document.',
  ].find(Boolean);
}

function document_extension(document = {}) {
  return normalize_text(document.extension).toUpperCase();
}
